import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Award, Users, Linkedin } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import SafeSEO from '@/components/SafeSEO';
import CtaSection from '@/components/CtaSection';

const About = () => {
  const { t } = useLanguage();

  const values = [
    {
      icon: Target,
      title: t('about.values.mission'),
      description: t('about.values.missionDesc'),
    },
    {
      icon: Eye,
      title: t('about.values.vision'),
      description: t('about.values.visionDesc'),
    },
    {
      icon: Award,
      title: t('about.values.excellence'),
      description: t('about.values.excellenceDesc'),
    },
    {
      icon: Users,
      title: t('about.values.collaboration'),
      description: t('about.values.collaborationDesc'),
    },
  ];

  const team = [
    {
      name: 'Maria Teresa Ruiz',
      title: t('about.team.member1.title'),
      credentials: t('about.team.member1.credentials'),
      bio: t('about.team.member1.bio'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/bd32f0adab837988083bdf0a7b3fce82.png',
      linkedin: '#',
    },
    {
      name: 'Floris Murillo Acuña',
      title: t('about.team.member2.title'),
      credentials: t('about.team.member2.credentials'),
      bio: t('about.team.member2.bio'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/afd4e23d9231ed30b3377361d965dab3.png',
      linkedin: '#',
    },
    {
      name: 'Silvia Dinora Del Águila',
      title: t('about.team.member3.title'),
      credentials: t('about.team.member3.credentials'),
      bio: t('about.team.member3.bio'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/ca80f475aefb45e835ca39c8f240d6e3.png',
      linkedin: '#',
    },
    {
      name: 'Marcela Nieto Palacio',
      title: t('about.team.member4.title'),
      credentials: t('about.team.member4.credentials'),
      bio: t('about.team.member4.bio'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/3af7b386d5edaad195f8f0529fba585c.jpg',
      linkedin: '#',
    },
    {
      name: 'Gisell Alvarez Larsen',
      title: t('about.team.member5.title'),
      credentials: t('about.team.member5.credentials'),
      bio: t('about.team.member5.bio'),
      image: 'https://horizons-cdn.hostinger.com/9442a93f-81a4-4857-bcb8-ca6535ca46c4/af55e393e88a1c4dd4066a24b1d6d0c4.png',
      linkedin: '#',
    },
  ];

  return (
    <>
      <SafeSEO
        title={t('about.title')}
        description={t('about.subtitle')}
        url="https://consulogic-gt-com-544311.hostingersite.com/quienes-somos"
      />

      <div className="pt-20">
        <section className="relative py-20 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] text-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('about.title')}</h1>
              <p className="text-xl text-white/90 leading-relaxed">
                {t('about.subtitle')}
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-6">
                  {t('about.historyTitle')}
                </h2>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {t('about.historyText1')}
                </p>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {t('about.historyText2')}
                </p>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {t('about.historyText3')}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  {t('about.historyText4')}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <img
                  className="w-full h-[500px] object-cover rounded-2xl shadow-2xl"
                  alt="Professional team meeting in modern office" src="https://images.unsplash.com/photo-1577962917302-cd874c4e31d2" />
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white/40 backdrop-blur-md p-8 rounded-3xl border border-white/20 shadow-xl shadow-gray-200/50 hover:shadow-2xl hover:translate-y-[-8px] transition-all duration-500 group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[#0A5F7F]/20 group-hover:rotate-6 transition-transform">
                    <value.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0A5F7F] mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-4">
                {t('about.teamTitle')}
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                {t('about.teamSubtitle')}
              </p>
            </motion.div>

            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl">
                {team.map((member, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: index * 0.2 }}
                    className="bg-white/60 backdrop-blur-xl rounded-[2rem] shadow-xl shadow-gray-200/50 overflow-hidden group transform hover:-translate-y-4 transition-all duration-500 border border-white/40"
                  >
                    <div className="relative overflow-hidden">
                      <img
                        className="w-full h-80 object-cover object-top transition-transform duration-700 group-hover:scale-110"
                        alt={`Profile of ${member.name}`}
                        src={member.image} />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A5F7F]/80 via-transparent to-transparent opacity-60"></div>
                      <div className="absolute bottom-0 left-0 p-6 z-10">
                        <h3 className="text-2xl font-bold text-white tracking-tight">{member.name}</h3>
                        {member.title && <p className="text-white/90 text-sm font-bold uppercase tracking-widest mt-1">{member.title}</p>}
                      </div>
                    </div>
                    <div className="p-8">
                      <p className="text-xs text-[#0A5F7F] font-bold uppercase tracking-tighter mb-4 opacity-70">{member.credentials}</p>
                      <p className="text-gray-600 text-sm leading-relaxed font-medium">{member.bio}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <CtaSection />
      </div>
    </>
  );
};

export default About;